import React, {useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {getWindow} from 'ssr-window';
import {FormElementError} from '@spothero/ui-backlog';
import {Box} from '@spothero/ui';
import StripeUtils from 'utils/stripe';
import {useSelector} from 'react-redux';
import trackCheckoutErrorMessageDisplayed from 'segment/events/checkout-error-message-displayed';
import isEmpty from 'lodash/isEmpty';

const window = getWindow();

const StripeCreditCardElement = ({labelText = 'Card Number', removeLabel}) => {
    const [stripeElementsCardError, setStripeElementsCardError] = useState(
        null
    );
    const userStatus = useSelector(({user}) => user.data.status);
    const {selected} = useSelector(state => state.spot);
    const {monthly: isMonthly} = useSelector(state => state.searchRequest);

    const onStripeCardElementChanged = evt => {
        setStripeElementsCardError(evt.error ? evt.error.message : null);
    };

    const cardElementRef = useRef(null);

    useEffect(() => {
        const createStripeCardElement = async () => {
            if (!window.StripeElements) {
                await StripeUtils.init();
            }

            if (!window.SHStripeCardElement) {
                /* eslint-disable require-atomic-updates */
                window.SHStripeCardElement = window.StripeElements.create(
                    'card',
                    {
                        style: {
                            base: {
                                fontFamily: '"Plus Jakarta Sans", sans-serif',
                                fontSize: '14px',
                                fontWeight: '300',
                                lineHeight: '20px',
                                color: '#5c7996',
                                '::placeholder': {
                                    color: '#cbd4de',
                                },
                            },
                        },
                    }
                );
                /* eslint-enable require-atomic-updates */
            }

            window.SHStripeCardElement.mount(cardElementRef.current);
            window.SHStripeCardElement.addEventListener(
                'change',
                onStripeCardElementChanged
            );
        };

        if (cardElementRef.current !== null) {
            createStripeCardElement();
        }
    }, []);

    useEffect(() => {
        // clean up
        return () => {
            if (window.SHStripeCardElement) {
                window.SHStripeCardElement.unmount();
                window.SHStripeCardElement.removeEventListener(
                    'change',
                    onStripeCardElementChanged
                );
                window.SHStripeCardElement.destroy();
                window.SHStripeCardElement = null;
            }
        };
    }, []);

    useEffect(() => {
        // if spot.selected doesn't exist in the store then this is in parking pass and we don't want to fire this event
        if (stripeElementsCardError && selected) {
            const {
                selectedRate,
                event_package: eventPackage,
                facility,
            } = selected;

            const isEvent = Boolean(selectedRate?.event_id);
            const isAirport = Boolean(facility?.airport);
            const isEventPackage = !isEmpty(eventPackage);

            trackCheckoutErrorMessageDisplayed({
                userStatus,
                isEvent,
                isMonthly,
                isAirport,
                isEventPackage,
                errorMessage: stripeElementsCardError,
                pageSection: 'Payment Details',
            });
        }
    }, [stripeElementsCardError, selected, isMonthly, userStatus]);

    return (
        <Box>
            {!removeLabel && (
                <Box
                    as="label"
                    display="block"
                    marginBottom={2}
                    fontWeight="semibold"
                    color={stripeElementsCardError ? 'error' : 'gray.dark'}
                >
                    {labelText}
                </Box>
            )}
            <Box
                ref={cardElementRef}
                width="100%"
                height={10}
                lineHeight={10}
                backgroundColor="white"
                backgroundImage="none"
                borderWidth="1px"
                borderStyle="solid"
                borderColor={stripeElementsCardError ? 'error' : 'gray.medium'}
                outline="none"
                appearance="none"
                fontSize="sm"
                paddingTop={2.5}
                paddingX={2.5}
                borderRadius="4px"
            />
            {stripeElementsCardError && (
                <FormElementError>{stripeElementsCardError}</FormElementError>
            )}
        </Box>
    );
};

StripeCreditCardElement.propTypes = {
    labelText: PropTypes.string,
    removeLabel: PropTypes.bool,
};

export default StripeCreditCardElement;
